import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { TBancos } from "../../../../../../types/api/financeiro/bancos";
import { TConfiguracaoBancaria, TIntegracaoBancaria } from "../../../../../../types/api/financeiro/configuracoes-bancarias";
import { TOpenModalConfigBancarias } from "..";
import SelectLojaComPesquisa, { TLoja } from "../../../../../../components/SelectsSimpleApiQuery/SelectLojaComPesquisa";
import SelectBancos from "../../../../../../components/SelectsSimpleApiQuery/SelectBancos";

export default function DialogCriacaoEditConfiguracoesBancarias({
    open,
    handleClose,
    defaultValue
}: DialogCriacaoEditConfiguracoesBancariasProps) {

    const queryClient = useQueryClient();

    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue?.defaultValue : null, [defaultValue])

    const methods = useForm<TFormConfiguracaoBancaria>()
    const { control, watch, setValue, handleSubmit, getFieldState } = methods;
    const [integracaoSelecionada, setIntegracaoSelecionada] = useState<TIntegracao>(null)

    //Querys e mutations
    const { data, isFetching } = useQuery({
        queryKey: ["pesquisar-bancos"],
        queryFn: () => axiosClient.get("financeiro/configuracoes/pesquisar-bancos", {
            params: {
                limit: 100,
                page: 1,
            }
        }),
        onSuccess: ({ data }) => {
            if (valoresIniciais) setValue('banco', valoresIniciais)
            if (valoresIniciais?.codigo) handleChangeBancoSelecionado(valoresIniciais?.codigo, data.data)
            if (valoresIniciais?.integracao) {
                let objetoIntegracao = valoresIniciais?.integracao
                setValue(`integracao_selecionada.nome`, objetoIntegracao.classe)
                let chaves = Object.keys(objetoIntegracao) as Array<keyof TIntegracaoBancaria>;
                chaves = chaves.filter(chave => chave !== 'classe');
                setIntegracaoSelecionada(chaves)
                chaves.forEach(item => {
                    const valor = objetoIntegracao[item];
                    setValue(`integracao_valores.${item}`, valor)
                });
            }
        },
        cacheTime: 1
    });
    const dataBancos: TBancos[] = useMemo(() => data?.data.data || [], [data])

    const { mutate: criarConfig, isLoading: isLoadingCriarConfig } = useMutation({
        mutationKey: 'cadastrar-configuracao-bancaria',
        mutationFn: (inputs: any) => axiosClient.post("financeiro/configuracoes/cadastrar-configuracao-bancaria", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar configuração bancária!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-configuracoes-bancarias"])
            handleClose()
            enqueueSnackbar('Cadastro da configuração realizada com sucesso!', { variant: 'success' });
        }
    })

    const { mutate: atualizarConfig, isLoading: isLoadingAttConfig } = useMutation({
        mutationKey: 'atualizar-configuracao-bancaria',
        mutationFn: (inputs: any) => axiosClient.put(`financeiro/configuracoes/atualizar-configuracao-bancaria/${valoresIniciais?.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao atualizar configuração!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-configuracoes-bancarias"])
            handleClose()
            enqueueSnackbar('Atualização da configuração realizada com sucesso!', { variant: 'success' });
        }
    })
    //

    //Valores importantes
    const bancoSelecionado = watch('banco_selecionado') ? watch('banco_selecionado') : null
    const possuiIntegracao: any = bancoSelecionado?.integracoes_possiveis ? bancoSelecionado?.integracoes_possiveis : null
    //

    //Funções manipuladoras
    const onSubmit = (data: TFormConfiguracaoBancaria) => {
        let JSONFormatado;
        if (data.integracao_selecionada && data.integracao_valores) {
            JSONFormatado = formatIntegracao(data.integracao_valores, data.integracao_selecionada.nome)
        }
        const params = {
            codigo: data.banco_selecionado.codigo,
            agencia: data.banco.agencia,
            agencia_digito: data.banco.agencia_digito,
            conta: data.banco.conta,
            conta_digito: data.banco.conta_digito,
            integracao: JSONFormatado
        }
        if (isEdit) {
            atualizarConfig(params)
        }
        if (!isEdit) {
            criarConfig(params)
        }
    }

    const handleChangeIntegracao = (value: string) => {
        setValue('integracao_valores', null)
        setValue(`integracao_selecionada.nome`, value)
        const selectedItem = possuiIntegracao[value]
        const valuesItem: string[] = selectedItem as string[]
        setIntegracaoSelecionada(valuesItem)
        valuesItem.forEach((value: string) =>
            setValue(`integracao_valores.${value}`, '')
        )
    }

    const handleChangeBancoSelecionado = (codigo: string, data: TBancos[]) => {
        const banco = data.find((banco) => banco.codigo === codigo)
        if (banco) {
            setValue('banco_selecionado', banco)
            setValue('integracao_selecionada', null)
            setValue('integracao_valores', {})
            setIntegracaoSelecionada(null)
        }
    }
    //

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {`${!isEdit ? "Adicionar" : "Editar"} configuração bancária`}
                </DialogTitle>

                {
                    (isLoadingAttConfig || isLoadingCriarConfig) &&
                    <Box display="flex" justifyContent="center" mt={4} mb={8}>
                        <CircularProgress />
                    </Box>
                }

                {
                    !isLoadingAttConfig && !isLoadingCriarConfig &&
                    <>
                        <Box
                            component={'form'}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 12}}>
                                        {
                                            isFetching &&
                                            <Box display={'flex'} justifyContent={'center'}>
                                                <CircularProgress />
                                            </Box>
                                        }
                                        {
                                            !isFetching &&
                                            <>
                                                <Controller
                                                    name="banco_selecionado"
                                                    control={control}
                                                    rules={{ required: 'Campo obrigatório' }}
                                                    render={({ field }) => (
                                                        <FormControl required fullWidth {...field} error={getFieldState('banco_selecionado').invalid}>
                                                            <SelectBancos
                                                                value={watch('banco_selecionado')}
                                                                onChange={(value) => {
                                                                    field.onChange(value)
                                                                }}
                                                            />
                                                        </FormControl>
                                                    )}
                                                />
                                            </>
                                        }
                                    </Grid>
                                    <Grid size={{xs: 6}}>
                                        <Controller
                                            name="banco.agencia"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.agencia}
                                                    label="Agência"
                                                    required
                                                    variant="outlined"
                                                    fullWidth
                                                    error={getFieldState('banco.agencia').invalid}
                                                    helperText={getFieldState('banco.agencia').error?.message || ""}
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="banco.conta"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.conta}
                                                    label="Conta"
                                                    required
                                                    variant="outlined"
                                                    sx={{ mt: 2 }}
                                                    fullWidth
                                                    error={getFieldState('banco.conta').invalid}
                                                    helperText={getFieldState('banco.conta').error?.message || ""}
                                                />
                                            )}
                                        />

                                    </Grid>
                                    <Grid size={{xs: 6}}>

                                        <Controller
                                            name="banco.agencia_digito"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.agencia_digito}
                                                    label="Dígito agência"
                                                    variant="outlined"
                                                    fullWidth
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="banco.conta_digito"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.conta_digito}
                                                    label="Dígito conta"
                                                    sx={{ mt: 2 }}
                                                    variant="outlined"
                                                    fullWidth
                                                />
                                            )}
                                        />

                                    </Grid>
                                    <Grid size={{xs: 12}}>
                                        <Divider sx={{ mb: 2 }} />
                                        {
                                            possuiIntegracao && Object.keys(possuiIntegracao).length > 0 &&
                                            <Controller
                                                name="integracao_selecionada.nome"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl fullWidth {...field} error={getFieldState('integracao_selecionada.nome').invalid}>
                                                        <InputLabel id="integracao-select">Selecionar Integração</InputLabel>
                                                        <Select
                                                            labelId="integracao-select"
                                                            value={field.value}
                                                            label="Selecionar Integração"
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                handleChangeIntegracao(e.target.value as string);
                                                            }}
                                                        >
                                                            {Object.keys(possuiIntegracao).map((key: string, index: number) => (
                                                                <MenuItem key={index} value={key}>
                                                                    {key}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                        }
                                    </Grid>
                                    {
                                        possuiIntegracao && integracaoSelecionada &&
                                        integracaoSelecionada.map((item: string, index) => {
                                            if (item === 'lojaPixInstantaneo') return (
                                                <Grid size={{md: 6}} key={index}>
                                                    <Controller
                                                        name={`integracao_valores.${item}`}
                                                        control={control}
                                                        rules={{ required: 'Campo obrigatório' }}
                                                        render={({ field }) => (
                                                            <SelectLojaComPesquisa
                                                            {...field}
                                                            required
                                                            label="lojaPixInstantaneo"
                                                            value={field.value}
                                                            onChange={(value) => {
                                                                field.onChange(value)
                                                            }}
                                                            multiple
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                            )
                                            return (
                                                <Grid size={{md: 6}} key={index}>
                                                    <Controller
                                                        name={`integracao_valores.${item}`}
                                                        control={control}
                                                        rules={{ required: 'Campo obrigatório' }}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                defaultValue={valoresIniciais?.conta}
                                                                label={item}
                                                                required
                                                                variant="outlined"
                                                                fullWidth
                                                                error={getFieldState(`integracao_valores.${item}`).invalid}
                                                                helperText={getFieldState(`integracao_valores.${item}`).error?.message || ""}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                            )
                                        })
                                    }

                                </Grid>
                                <Box mt={2} width={'100%'} display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={2}>
                                    <Button
                                        onClick={handleClose}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        autoFocus
                                        disabled={isLoadingCriarConfig || isLoadingAttConfig}

                                    >
                                        {
                                            (isLoadingCriarConfig || isLoadingAttConfig) &&
                                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                        }
                                        {!isEdit ? "Adicionar" : "Atualizar"}
                                    </Button>
                                </Box>
                            </DialogContent>
                        </Box>
                    </>
                }
            </Dialog>
        </FormProvider>
    )
}

const formatIntegracao = (obj: {}, classe: string) => {
    const objeto = {
        classe,
        ...Object.fromEntries(Object.entries(obj).map(([chave, valor]) => {
            if (chave === 'lojaPixInstantaneo') {
                return [chave, Array.isArray(valor) ? valor.map((item: TLoja) => ({ id: item.id, nome: item.nome })) : valor]
            }
        return [chave, valor]
        }))
    }
    return JSON.stringify(objeto);
}

interface DialogCriacaoEditConfiguracoesBancariasProps {
    open: boolean;
    handleClose: () => void;
    defaultValue: TOpenModalConfigBancarias
}

type TFormConfiguracaoBancaria = {
    banco_selecionado: TBancos,
    banco: TConfiguracaoBancaria,
    integracao_selecionada: {
        [key: string]: {},
        nome: string
    } | null,
    integracao_valores: {
        [key: string]: any
    } | null,
}

type TIntegracao = string[] | null



